import { z } from 'zod';

export const CopilotModeSchema = z.enum(['agent', 'plan', 'ask']);

export type CopilotMode = z.infer<typeof CopilotModeSchema>;

export const COPILOT_MODE_OPTIONS = CopilotModeSchema.options;

export const DEFAULT_COPILOT_MODE: CopilotMode = 'agent';

export type CopilotModeCapabilities = {
  operatorSubAgent: boolean;
  objectCreatorSubAgent: boolean;
  writeTools: boolean;
  backgroundAttack: boolean;
  createMemory: boolean;
  loginTool: boolean;
  proactivePlanning: boolean;
  planArtifact: boolean;
};

export const COPILOT_MODE_CAPABILITIES: Record<
  CopilotMode,
  CopilotModeCapabilities
> = {
  agent: {
    operatorSubAgent: true,
    objectCreatorSubAgent: true,
    writeTools: true,
    backgroundAttack: true,
    createMemory: true,
    loginTool: true,
    proactivePlanning: true,
    planArtifact: false,
  },
  plan: {
    operatorSubAgent: false,
    objectCreatorSubAgent: false,
    writeTools: false,
    backgroundAttack: false,
    createMemory: false,
    loginTool: false,
    proactivePlanning: true,
    planArtifact: true,
  },
  ask: {
    operatorSubAgent: false,
    objectCreatorSubAgent: false,
    writeTools: false,
    backgroundAttack: false,
    createMemory: false,
    loginTool: false,
    proactivePlanning: false,
    planArtifact: false,
  },
};

const COPILOT_MODE_RANK: Record<CopilotMode, number> = {
  ask: 0,
  plan: 1,
  agent: 2,
};

export const parseCopilotMode = ({
  value,
}: {
  value: unknown;
}): CopilotMode => {
  const parsed = CopilotModeSchema.safeParse(value);

  return parsed.success ? parsed.data : DEFAULT_COPILOT_MODE;
};

export const isCopilotMode = (value: string): value is CopilotMode =>
  CopilotModeSchema.safeParse(value).success;

export const getCopilotModeCapabilities = ({
  mode,
}: {
  mode: CopilotMode;
}): CopilotModeCapabilities => COPILOT_MODE_CAPABILITIES[mode];

export const isCopilotModeBroadening = ({
  fromMode,
  toMode,
}: {
  fromMode: CopilotMode;
  toMode: CopilotMode;
}): boolean => COPILOT_MODE_RANK[toMode] > COPILOT_MODE_RANK[fromMode];
